import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/reducers';
import {
	fetchOpportunitiesSettings,
	setOpportunityStatusLabel,
	setAllOpportunityStatusLabels,
	persistOpportunityStatusLabel,
} from '@/actions/opportunities';

// Keep this union in sync with backend statuses. Do not change without backend support.
export type OpportunityStatus =
	| 'Interested'
	| 'In communication'
	| 'Follow up'
	| 'Won'
	| 'Closed';

const defaultLabels: Record<OpportunityStatus, string> = {
	Interested: 'Interested',
	'In communication': 'In communication',
	'Follow up': 'Follow up',
	Won: 'Won',
	Closed: 'Closed',
};

export default function useOpportunityStatusLabels() {
	const dispatch = useDispatch();
	const labels = useSelector(
		(state: RootState) => state.opportunities?.statusLabels
	) as Partial<Record<OpportunityStatus, string>> | undefined;

	useEffect(() => {
		// Fetch latest settings on mount
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(dispatch as any)(fetchOpportunitiesSettings());
	}, [dispatch]);

	const setLabel = useCallback(
		(status: OpportunityStatus, newLabel: string) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(dispatch as any)(
				setOpportunityStatusLabel(
					status,
					newLabel.trim() || defaultLabels[status]
				)
			);

			// persist change in the background
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(dispatch as any)(
				persistOpportunityStatusLabel({
					status,
					label: newLabel.trim() || defaultLabels[status],
				})
			);
		},
		[dispatch]
	);

	const resetLabels = useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(dispatch as any)(setAllOpportunityStatusLabels({ ...defaultLabels }));
	}, [dispatch]);

	const getLabel = useCallback(
		(status: OpportunityStatus) =>
			(labels && labels[status]) || defaultLabels[status] || status,
		[labels]
	);

	return useMemo(
		() => ({
			labels:
				(labels as Record<OpportunityStatus, string>) || defaultLabels,
			setLabel,
			resetLabels,
			getLabel,
			defaultLabels,
		}),
		[labels, setLabel, resetLabels, getLabel]
	);
}
